import { Fragment, memo, useMemo, useState } from 'react';
import { FieldArray, FormSection } from 'redux-form';

import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { CombinedStepType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import ToggleRowFields from 'dashboard/components/FormControls/ToggleRowFields';
import { InquiryTemplateVersionCombinedStep } from 'dashboard/models/inquiry-template-version';
import TabBar, { Tab } from 'design-system/components/TabBar';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import DeviceHandoffOption from 'lib/interfaces/DeviceHandoffOption';
import { DocumentAllowedFileType, UiStepType } from 'lib/interfaces/StepsConfig';
import { isRequired, Rule, withMessage } from 'lib/utils/formValidation';

type CaptureOptionsFieldName =
  | 'enabled-capture-options-desktop'
  | 'enabled-capture-options-mobile'
  | 'enabled-capture-options-native-mobile';

// repeated strings for toggle display
export const TOGGLE_LABELS_DESKTOP: Record<
  CombinedStepType,
  Record<string, { title: string; description: string }>
> = {
  [UiStepType.GovernmentId]: {
    web_camera: {
      title: 'Guided capture',
      description: 'Recommended option - capture live images for verification',
    },
    upload: {
      title: 'File upload',
      description: 'Allow file uploads from device',
    },
    device_handoff: {
      title: 'Device handoff',
      description: 'Configure how users can continue the flow on other devices',
    },
  },
  [UiStepType.Selfie]: {
    web_camera: {
      title: 'Guided capture',
      description: 'Recommended option - capture live images for verification',
    },
    device_handoff: {
      title: 'Device handoff',
      description: 'Configure how users can continue the flow on other devices',
    },
  },
  [UiStepType.SelfieReflection]: {
    web_camera: {
      title: 'Guided capture',
      description: 'Recommended option - capture live images for verification',
    },
    device_handoff: {
      title: 'Device handoff',
      description: 'Configure how users can continue the flow on other devices',
    },
  },
  [UiStepType.Document]: {
    web_camera: {
      title: 'Guided capture',
      description: 'Recommended option - capture live images for verification',
    },
    upload: {
      title: 'File upload',
      description: 'Allow file uploads from device',
    },
    device_handoff: {
      title: 'Device handoff',
      description: 'Configure how users can continue the flow on other devices',
    },
  },
};

export const TOGGLE_LABELS_MOBILE: Record<
  CombinedStepType,
  Record<string, { title: string; description: string }>
> = {
  [UiStepType.GovernmentId]: {
    web_camera: {
      title: 'Guided capture',
      description: 'Recommended option - capture live images for verification',
    },
    mobile_camera: {
      title: 'Unguided capture',
      description:
        'Use native mobile device camera if guided camera cannot load or is not configured',
    },
    upload: {
      title: 'File upload',
      description: 'Allow file uploads from device',
    },
    device_handoff: {
      title: 'Device handoff',
      description: 'Configure how users can continue the flow on other devices',
    },
  },
  [UiStepType.Selfie]: {
    web_camera: {
      title: 'Guided capture',
      description: 'Recommended option - capture live images for verification',
    },
    device_handoff: {
      title: 'Device handoff',
      description: 'Configure how users can continue the flow on other devices',
    },
  },
  [UiStepType.SelfieReflection]: {
    web_camera: {
      title: 'Guided capture',
      description: 'Recommended option - capture live images for verification',
    },
    device_handoff: {
      title: 'Device handoff',
      description: 'Configure how users can continue the flow on other devices',
    },
  },
  [UiStepType.Document]: {
    web_camera: {
      title: 'Guided capture',
      description: 'Recommended option - capture live images for verification',
    },
    mobile_camera: {
      title: 'Unguided capture',
      description:
        'Use native mobile device camera if guided camera cannot load or is not configured',
    },
    upload: {
      title: 'File upload',
      description: 'Allow file uploads from device',
    },
    device_handoff: {
      title: 'Device handoff',
      description: 'Configure how users can continue the flow on other devices',
    },
  },
};

export const TOGGLE_LABELS_MOBILE_NATIVE: Record<
  CombinedStepType,
  Record<string, { title: string; description: string }>
> = {
  [UiStepType.GovernmentId]: {
    mobile_camera: {
      title: 'Guided capture',
      description: 'Recommended option - capture live images for verification',
    },
    upload: {
      title: 'File upload',
      description: 'Allow file uploads from device',
    },
  },
  // Capture method selection is only supported for GovermentId steps
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [UiStepType.Selfie]: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [UiStepType.Document]: undefined,
};

// SMS device handoff is deprecated
export const DEVICE_HANDOFF_LABELS: Record<
  DeviceHandoffOption.Email | DeviceHandoffOption.Qr,
  { description: string }
> = {
  [DeviceHandoffOption.Email]: {
    description: 'Send Email',
  },
  [DeviceHandoffOption.Qr]: {
    description: 'Scan QR code',
  },
};

/**
 * @deprecated
 */
export const DEVICE_HANDOFF_LABELS_WITH_SMS: Record<DeviceHandoffOption, { description: string }> =
  {
    [DeviceHandoffOption.Email]: {
      description: 'Send Email',
    },
    [DeviceHandoffOption.Sms]: {
      description: 'Send SMS',
    },
    [DeviceHandoffOption.Qr]: {
      description: 'Scan QR code',
    },
  };

const validate: Record<'enabled-capture-options' | 'device-handoff-options', Rule[]> = {
  'enabled-capture-options': [withMessage('At least one capture method is required.', isRequired)],
  'device-handoff-options': [
    withMessage('At least one device handoff method is required.', isRequired),
  ],
};

interface FormSectionCaptureConfigProps {
  name: string;
  step: InquiryTemplateVersionCombinedStep;
  enabledFileTypes?: DocumentAllowedFileType[];
}

const FormSectionCaptureConfig = ({
  name,
  step,
  enabledFileTypes,
}: FormSectionCaptureConfigProps) => {
  const editableForFreeTier = useIsEditableWithOptions({
    accessTier: EditorAccessTier.InquiriesFree,
  });
  const {
    'enabled-capture-options-desktop': captureOptionsDesktop,
    'enabled-capture-options-mobile': captureOptionsMobile,
  } = step.config;
  const tabsConfig = useMemo(
    () => [
      {
        name: 'Desktop web',
        contents: (
          <ToggleRowSection
            step={step}
            fieldCollectionName='enabled-capture-options-desktop'
            deviceHandoffEnabled={captureOptionsDesktop.includes('device_handoff')}
            disabled={!editableForFreeTier}
            enabledFileTypes={enabledFileTypes}
          />
        ),
      },
      {
        name: 'Mobile web',
        contents: (
          <ToggleRowSection
            step={step}
            fieldCollectionName='enabled-capture-options-mobile'
            deviceHandoffEnabled={captureOptionsMobile.includes('device_handoff')}
            disabled={!editableForFreeTier}
            enabledFileTypes={enabledFileTypes}
          />
        ),
      },
      ...(step.type === UiStepType.GovernmentId
        ? [
            {
              name: 'Mobile native',
              contents: (
                <ToggleRowSection
                  step={step}
                  fieldCollectionName='enabled-capture-options-native-mobile'
                  deviceHandoffEnabled={false}
                  disabled={!editableForFreeTier}
                  enabledFileTypes={enabledFileTypes}
                />
              ),
            },
          ]
        : []),
    ],
    [editableForFreeTier, captureOptionsDesktop, captureOptionsMobile, step, enabledFileTypes],
  );

  const [selected, setSelected] = useState(0);

  return (
    <FormSection name={name} component={Fragment}>
      <TabBar>
        {tabsConfig.map((tabConfig, idx) => (
          <Tab
            key={`tab-${tabConfig.name}`}
            selected={selected === idx}
            onClick={() => setSelected(idx)}
          >
            {tabConfig.name}
          </Tab>
        ))}
      </TabBar>
      <div>{tabsConfig[selected].contents}</div>
    </FormSection>
  );
};

interface ToggleRowSectionProps {
  step: InquiryTemplateVersionCombinedStep;
  fieldCollectionName: CaptureOptionsFieldName;
  deviceHandoffEnabled: boolean;
  disabled: boolean;
  enabledFileTypes?: DocumentAllowedFileType[];
}

const ToggleRowSection = memo(
  ({
    step,
    fieldCollectionName,
    deviceHandoffEnabled,
    disabled,
    enabledFileTypes,
  }: ToggleRowSectionProps) => {
    const hideSmsDeviceHandoffOptionInverse = useFeatureFlag(
      'inquiry:hide-sms-device-handoff-option-inverse',
    );

    const deviceHandoffLabels = hideSmsDeviceHandoffOptionInverse
      ? DEVICE_HANDOFF_LABELS_WITH_SMS
      : DEVICE_HANDOFF_LABELS;

    let toggleLabels;
    if (fieldCollectionName === 'enabled-capture-options-desktop') {
      toggleLabels = TOGGLE_LABELS_DESKTOP[step.type];
    } else if (fieldCollectionName === 'enabled-capture-options-mobile') {
      toggleLabels = TOGGLE_LABELS_MOBILE[step.type];
    } else if (fieldCollectionName === 'enabled-capture-options-native-mobile') {
      toggleLabels = TOGGLE_LABELS_MOBILE_NATIVE[step.type];
    }

    // omit upload capture option for selfie step
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const options = Object.keys(toggleLabels);

    return (
      <>
        <FieldArray
          key={fieldCollectionName}
          name={fieldCollectionName}
          component={ToggleRowFields}
          validate={validate['enabled-capture-options']}
          options={options}
          labels={toggleLabels}
          disabled={disabled}
          enabledFileTypes={enabledFileTypes}
        />
        {deviceHandoffEnabled && (
          <FieldArray
            key={`device-handoff-options_${step.name}`}
            name='device-handoff-options'
            component={ToggleRowFields}
            validate={validate['device-handoff-options']}
            options={Object.keys(deviceHandoffLabels)}
            labels={deviceHandoffLabels}
            disabled={disabled}
            subToggle={true}
            enabledFileTypes={enabledFileTypes}
          />
        )}
      </>
    );
  },
);

ToggleRowSection.displayName = 'ToggleRowSection';

export default FormSectionCaptureConfig;
